import React, { useEffect, useState } from 'react';
import { IonContent, IonFooter } from '@ionic/react';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';

import { input } from '$gbusiness/helpers';
import intl from '$intl';
import FileModel from '$fbusiness/models/file';
import { SPACE } from '$gstyles';
import { Flex, PageWrapper } from '$gstyles/wrapper';
import { Button } from '$gcomponents/primitives';

import FILE_EDIT_FORM from './fileEditForm';
import { fetchFolders } from '$fbusiness/redux/file/folderActions';
import { flattenFolders } from '$fbusiness/models/fileFolder';
import { FormSection, dialog } from '$gcomponents/reusables';
import { fileActions } from '$fbusiness/redux/file';
import { fetchFilePasscode } from '$fbusiness/redux/file/actions';
import StoreModel from '$fbusiness/models/store';
import StoreSelector from './storeSelector';

interface FileEditDetailsProps {
  file: FileModel;
  onClose: Function;
  onUpdateFile: Function;
  stores: Array<StoreModel>;
}

const FileEditDetails: React.FC<FileEditDetailsProps> = ({ stores, file, onClose, onUpdateFile }) => {
  const dispatch = useDispatch();
  const [selectStores, setSelectStores] = useState<any>(null);
  // Decrypted passcode for protected files. Fetched lazily on open so that
  // listing responses never carry plaintext passcodes.
  const [currentPasscode, setCurrentPasscode] = useState<string>('');

  useEffect(() => {
    if (file) setSelectStores(file.storeIds || null);
  }, [file]);

  useEffect(() => {
    if (file?.id && file?.isProtected) {
      fetchFilePasscode(file.id).then((p) => setCurrentPasscode(p || ''));
    } else {
      setCurrentPasscode('');
    }
  }, [file?.id, file?.isProtected]);

  const [folderOptions, setFolderOptions] = useState<Array<any>>([]);
  useEffect(() => {
    (async () => {
      const tree = await fetchFolders();
      setFolderOptions(
        flattenFolders(tree).map(({ folder, depth }) => ({
          value: folder.id,
          labelText: `${'\u00a0\u00a0'.repeat(depth)}${depth ? '└ ' : ''}${folder.name}`,
        })),
      );
    })();
  }, []);

  if (!file) return null;

  const initialValues = {
    id: file?.id,
    name: file?.name,
    description: file?.description,
    folderId: file?.folderId || 0,
    isProtected: !!file?.isProtected,
    passcode: currentPasscode,
  };
  const onSubmitEdit = async values => {
    if (values.isProtected && !(values.passcode || '').trim()) {
      dialog.alert({ text: intl('SCREEN.FILES.COLS.PASSCODE_REQUIRED') });
      return;
    }
    await dispatch(
      fileActions.updateFile({
        ...values,
        storeIds: selectStores,
      }),
    );
    onUpdateFile();
  };

  const onStoreSelect = values => {
    setSelectStores(values);
  };

  const FORM = FILE_EDIT_FORM(folderOptions);
  const validateForm = values => input.validateError(FORM, values);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validate={validateForm}
      onSubmit={values => {
        onSubmitEdit(values);
      }}>
      {formik => (
        <>
          <IonContent>
            <PageWrapper>
              <StoreSelector stores={stores} onChange={onStoreSelect} values={selectStores} />
              <FormSection formik={formik} FORM={FORM} />
            </PageWrapper>
          </IonContent>
          <IonFooter>
            <Flex padding={SPACE.MEDIUM} justifyContent="flex-end">
              <Button onClick={formik.handleSubmit}>{intl('BUTTON.SUBMIT')}</Button>
            </Flex>
          </IonFooter>
        </>
      )}
    </Formik>
  );
};

export default FileEditDetails;
